import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Citation } from 'src/app/models/analyst-output.model';
import { isPlainLeftClick } from 'src/app/shared/utils/citation-click.util';
import { getRichPreviewComponent } from 'src/app/shared/utils/citation-preview.util';
import { sanitizeCitationRoute } from 'src/app/shared/utils/citation-resolver.util';

/** Cross-tree signal from any citation marker (prose, table, chart, or sources list) up to whichever
 * host owns the citation panel's flyout. Markers sit several levels below AiAnalystComponent
 * (analyst-table/analyst-chart or analyst-sources -> analyst-output -> chat-thread), so a shared
 * service avoids threading an @Output through every intermediate level.
 *
 * Also the single place that decides whether a citation is worth a panel at all: only the entity
 * types with a real preview component get one, everything else goes straight to its records. */
@Injectable({
  providedIn: 'root',
})
export class CitationPanelService {
  private readonly citationRequested = new Subject<Citation>();
  readonly citationRequested$ = this.citationRequested.asObservable();
  private readonly citationRevealRequested = new Subject<Citation>();
  readonly citationRevealRequested$ = this.citationRevealRequested.asObservable();

  constructor(private readonly router: Router) {}

  /** Opens the preview panel for citations that have a preview to show, and navigates straight to the
   * cited records for the ones that don't. A citation with neither, or one whose route does not
   * survive `sanitizeCitationRoute`, still opens the panel, which at least names the record. */
  open(citation: Citation): void {
    const safeRoute = citation.route ? sanitizeCitationRoute(citation.route) : '';
    if (!getRichPreviewComponent(citation) && safeRoute) {
      void this.router.navigateByUrl(safeRoute);
      return;
    }
    this.citationRequested.next(citation);
  }

  /** A plain click is handled in-app by `open` above; a modified click (ctrl/cmd/shift/alt, or a
   * non-primary button) is left alone so the citation's real `href` still opens the record in a new
   * tab like any other link. */
  openFromClick(event: MouseEvent, citation: Citation): void {
    if (this.claimPlainClick(event)) {
      this.open(citation);
    }
  }

  openMarker(citation: Citation): void {
    this.citationRevealRequested.next(citation);
    this.open(citation);
  }

  openMarkerFromClick(event: MouseEvent, citation: Citation): void {
    if (this.claimPlainClick(event)) {
      this.openMarker(citation);
    }
  }

  private claimPlainClick(event: MouseEvent): boolean {
    if (!isPlainLeftClick(event)) {
      return false;
    }
    event.preventDefault();
    return true;
  }
}
